import { Button, Input, Modal } from "@/components";
import { useTranslation } from "@/contexts/LanguageContext";
import { Link2, RefreshCw } from "lucide-react";
import React from "react";

interface UtmFields {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
}

const UTM_KEYS: (keyof UtmFields)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

const EMPTY_FIELDS: UtmFields = {
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_content: "",
  utm_term: "",
};

// Converte a querystring crua (`utm_source=x&utm_medium=y...`) guardada em
// `trackingParam` de volta para campos separados, e vice-versa — mantém o
// state do contexto como uma única string (compatível com o resto do app,
// ex: `url_tags` enviado pra API do Meta), só a UI do modal trabalha com
// os campos individuais.
export function parseTrackingParam(value: string): UtmFields {
  const fields = { ...EMPTY_FIELDS };
  if (!value) return fields;
  const params = new URLSearchParams(value);
  UTM_KEYS.forEach((key) => {
    const v = params.get(key);
    if (v) fields[key] = v;
  });
  return fields;
}

export function buildTrackingParam(fields: UtmFields): string {
  return UTM_KEYS.filter((key) => fields[key]?.trim())
    .map((key) => `${key}=${encodeURIComponent(fields[key].trim())}`)
    .join("&");
}

const FIELD_LABELS: Record<
  keyof UtmFields,
  { label: string; placeholder: string; hint: string }
> = {
  utm_source: {
    label: "Origem (utm_source)",
    placeholder: "ex: meta, google, linkedin",
    hint: "De onde vem o tráfego — geralmente a plataforma ou o nome do cliente.",
  },
  utm_medium: {
    label: "Mídia (utm_medium)",
    placeholder: "ex: cpc, paid_social",
    hint: "Tipo de canal — cpc, paid_social, email, etc.",
  },
  utm_campaign: {
    label: "Campanha (utm_campaign)",
    placeholder: "ex: black_friday_2026",
    hint: "Identifica a campanha específica.",
  },
  utm_content: {
    label: "Conteúdo (utm_content)",
    placeholder: "ex: video_1, banner_azul",
    hint: "Diferencia variações do mesmo anúncio (opcional).",
  },
  utm_term: {
    label: "Termo (utm_term)",
    placeholder: "ex: tenis-corrida",
    hint: "Usado normalmente em campanhas de busca paga (opcional).",
  },
};

interface UtmParamsModalProps {
  open: boolean;
  onClose: () => void;
  trackingParam: string;
  linkUrl: string;
  onSave: (value: string) => void;
  onRestoreDefault: () => void;
}

const UtmParamsModal: React.FC<UtmParamsModalProps> = ({
  open,
  onClose,
  trackingParam,
  linkUrl,
  onSave,
  onRestoreDefault,
}) => {
  const { t } = useTranslation();
  const [fields, setFields] = React.useState<UtmFields>(EMPTY_FIELDS);

  // Recarrega os campos a partir do valor atual sempre que o modal abre —
  // evita mostrar um estado obsoleto se o usuário abrir, fechar sem salvar
  // e abrir de novo.
  React.useEffect(() => {
    if (open) setFields(parseTrackingParam(trackingParam));
  }, [open, trackingParam]);

  const assembled = buildTrackingParam(fields);
  const previewUrl = linkUrl
    ? `${linkUrl}${linkUrl.includes("?") ? "&" : "?"}${assembled}`
    : `?${assembled}`;

  const handleSave = () => {
    onSave(assembled);
    onClose();
  };

  const handleRestore = () => {
    onRestoreDefault();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Parâmetros UTM")}
      subtitle={t(
        "Controle exatamente o que vai em cada parâmetro de rastreamento da URL.",
      )}
      icon={<Link2 className="w-5 h-5" />}
      maxWidth="lg"
      closeOnOverlayClick
      footer={
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRestore}
            className="border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-purple-700 font-bold text-[10px] uppercase min-h-[32px]! h-8 px-2.5 rounded-lg shadow-none"
            icon={<RefreshCw className="w-3.5 h-3.5" />}
          >
            {t("Restaurar Padrão")}
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t("Cancelar")}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSave}
            >
              {t("Salvar")}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3.5">
        {UTM_KEYS.map((key) => (
          <Input
            key={key}
            label={t(FIELD_LABELS[key].label)}
            hint={t(FIELD_LABELS[key].hint)}
            value={fields[key]}
            onChange={(e) =>
              setFields((f) => ({ ...f, [key]: e.target.value }))
            }
            placeholder={FIELD_LABELS[key].placeholder}
            inputSize="sm"
            className="text-xs font-mono rounded-xl border-purple-200"
          />
        ))}

        <div className="pt-2 border-t border-purple-100 space-y-1">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            {t("Prévia da URL Final")}
          </span>
          <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-200 text-[11px] font-mono text-gray-600 break-all">
            {assembled
              ? previewUrl
              : t("Nenhum parâmetro preenchido — nenhuma UTM será enviada.")}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UtmParamsModal;
